<script lang="ts">
  import Header from '../../app/layout/Header.svelte';
  import { addCategory, deleteCategory, listCategories, renameCategory } from '../../data/categories';
  import type { Category } from '../../data/types';

  let categories = $state<Category[]>([]);
  let loading = $state(true);
  let errorMessage = $state('');

  let newCategoryName = $state('');
  let addingInProgress = $state(false);

  let editingId = $state<string | null>(null);
  let editingName = $state('');

  async function loadCategories() {
    loading = true;
    errorMessage = '';
    try {
      categories = await listCategories();
    } catch (err) {
      console.error(err);
      errorMessage = 'カテゴリの読み込みに失敗しました。';
    } finally {
      loading = false;
    }
  }

  $effect(() => {
    loadCategories();
  });

  async function handleAddCategory(event: SubmitEvent) {
    event.preventDefault();
    const name = newCategoryName.trim();
    if (!name || addingInProgress) return;

    addingInProgress = true;
    errorMessage = '';
    try {
      await addCategory(name);
      newCategoryName = '';
      await loadCategories();
    } catch (err) {
      console.error(err);
      errorMessage = 'カテゴリの追加に失敗しました。';
    } finally {
      addingInProgress = false;
    }
  }

  function startEdit(category: Category) {
    editingId = category.id;
    editingName = category.name;
    errorMessage = '';
  }

  function cancelEdit() {
    editingId = null;
    editingName = '';
  }

  async function handleRename(event: SubmitEvent, id: string) {
    event.preventDefault();
    const name = editingName.trim();
    if (!name) return;

    errorMessage = '';
    try {
      await renameCategory(id, name);
      editingId = null;
      editingName = '';
      await loadCategories();
    } catch (err) {
      console.error(err);
      errorMessage = 'カテゴリ名の変更に失敗しました。';
    }
  }

  async function handleDelete(category: Category) {
    errorMessage = '';
    const confirmed = confirm(`「${category.name}」を削除しますか？`);
    if (!confirmed) return;

    try {
      const result = await deleteCategory(category.id);
      if (!result.ok) {
        if (result.reason === 'in-use') {
          errorMessage = `「${category.name}」は支出記録が存在するため削除できません。`;
        } else {
          errorMessage = 'カテゴリの削除に失敗しました。';
        }
        return;
      }
      await loadCategories();
    } catch (err) {
      console.error(err);
      errorMessage = 'カテゴリの削除に失敗しました。';
    }
  }
</script>

<Header title="カテゴリ管理" showBack={true} backTo="/" />

<main class="page">
  <form class="add-form" onsubmit={handleAddCategory}>
    <input
      type="text"
      class="text-input"
      placeholder="新しいカテゴリ名"
      bind:value={newCategoryName}
      disabled={addingInProgress}
    />
    <button type="submit" class="primary-button" disabled={addingInProgress || !newCategoryName.trim()}>
      追加
    </button>
  </form>

  {#if errorMessage}
    <p class="error-text">{errorMessage}</p>
  {/if}

  {#if loading}
    <p class="muted-text">読み込み中...</p>
  {:else if categories.length === 0}
    <p class="muted-text">カテゴリが登録されていません。上のフォームから追加してください。</p>
  {:else}
    <ul class="category-list">
      {#each categories as category (category.id)}
        <li class="category-item">
          {#if editingId === category.id}
            <form class="edit-form" onsubmit={(e) => handleRename(e, category.id)}>
              <input
                type="text"
                class="text-input"
                bind:value={editingName}
                aria-label="カテゴリ名"
              />
              <button type="submit" class="primary-button" disabled={!editingName.trim()}>
                保存
              </button>
              <button type="button" class="secondary-button" onclick={cancelEdit}>
                キャンセル
              </button>
            </form>
          {:else}
            <span class="category-item__name">{category.name}</span>
            <div class="category-item__actions">
              <button type="button" class="secondary-button" onclick={() => startEdit(category)}>
                名称変更
              </button>
              <button type="button" class="danger-button" onclick={() => handleDelete(category)}>
                削除
              </button>
            </div>
          {/if}
        </li>
      {/each}
    </ul>
  {/if}
</main>

<style>
  .page {
    padding: 1rem;
    max-width: 640px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .add-form {
    display: flex;
    gap: 0.5rem;
  }

  .edit-form {
    display: flex;
    gap: 0.5rem;
    flex: 1;
    flex-wrap: wrap;
  }

  .text-input {
    flex: 1;
    min-width: 0;
    padding: 0.5rem 0.7rem;
    border-radius: var(--radius-md);
    border: 1px solid var(--color-border);
    background-color: var(--color-bg);
    color: var(--color-text);
  }

  .text-input:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 1px;
  }

  .primary-button {
    padding: 0.5rem 0.9rem;
    border-radius: var(--radius-md);
    border: 1px solid var(--color-primary);
    background-color: var(--color-primary);
    color: var(--color-primary-text);
    cursor: pointer;
    font-size: 0.9rem;
  }

  .primary-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .secondary-button {
    padding: 0.5rem 0.9rem;
    border-radius: var(--radius-md);
    border: 1px solid var(--color-border);
    background-color: var(--color-bg-elevated);
    color: var(--color-text);
    cursor: pointer;
    font-size: 0.9rem;
  }

  .danger-button {
    padding: 0.5rem 0.9rem;
    border-radius: var(--radius-md);
    border: 1px solid var(--color-danger);
    background-color: var(--color-danger-bg);
    color: var(--color-danger);
    cursor: pointer;
    font-size: 0.9rem;
  }

  .muted-text {
    color: var(--color-text-muted);
  }

  .error-text {
    color: var(--color-danger);
  }

  .category-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
  }

  .category-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border-radius: var(--radius-lg);
    border: 1px solid var(--color-border);
    background-color: var(--color-bg-elevated);
    box-shadow: 0 1px 2px var(--color-shadow);
    flex-wrap: wrap;
  }

  .category-item__name {
    font-size: 1rem;
    font-weight: 600;
    color: var(--color-text);
  }

  .category-item__actions {
    display: flex;
    gap: 0.5rem;
  }
</style>
